import React, { useState } from 'react';
import { FiX, FiAlertTriangle } from 'react-icons/fi';
import { UserListItem } from '../../types/user-management';
import { logger } from '../../utils';

interface DeleteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (userId: string) => Promise<void>;
  user: UserListItem | null;
}

const DeleteUserModal: React.FC<DeleteUserModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  user,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!user) return;

    try {
      setIsDeleting(true);
      setError(null);
      await onConfirm(user.id);
      onClose();
    } catch (err) {
      logger.error('ADMIN', 'Failed to delete user', { error: err });
      setError('Failed to delete user. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-tedy-black">Delete User</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-center mb-4 text-red-600">
            <FiAlertTriangle className="h-12 w-12" />
          </div>

          <div className="text-center mb-6">
            <p className="text-lg font-medium text-gray-900 mb-2">
              Are you sure you want to delete this user?
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="font-medium text-gray-900">{user.name}</p>
              <p className="text-gray-500">{user.email}</p>
              <p className="text-gray-500">
                {user.role === 'tedy-partner' ? 'Partner' : 'Student'}
                {user.organization && ` - ${user.organization}`}
              </p>
            </div>
            <p className="text-sm text-gray-500">
              This action cannot be undone. All associated data will be permanently deleted.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 p-4 rounded-md mb-6">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="btn-danger"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete User'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteUserModal;
